import { Component, Input, Output, EventEmitter, OnChanges, AfterViewInit, ViewChildren, QueryList, ViewChild } from '@angular/core';
import { MapMarker as GoogleMapMarker } from '@angular/google-maps';
import { GoogleMap } from '@angular/google-maps';

export interface MapMarker {
  position: { lat: number; lng: number };
  type?: 'start' | 'end' | 'default';
  options?: google.maps.MarkerOptions;
  infoWindowContent?: string;
}

@Component({
    selector: 'pv-google-map',
    templateUrl: './google-map.component.html',
    styleUrls: ['./google-map.component.scss'],
    standalone: false
})
export class GoogleMapComponent implements OnChanges, AfterViewInit {
  @Input() center: { lat: number; lng: number } = { lat: 48.864716, lng: 2.349014 };
  @Input() zoom: number = 5;
  @Input() markers: MapMarker[] = [];
  @Input() origin: { lat: number; lng: number };
  @Input() destination: { lat: number; lng: number };

  @Output() markerDragEnd = new EventEmitter<{ event: google.maps.MapMouseEvent; marker: MapMarker }>();
  @Output() directionsCalculated = new EventEmitter<any>();

  @ViewChildren(GoogleMapMarker) mapMarkers!: QueryList<GoogleMapMarker>;
  @ViewChild(GoogleMap) map!: GoogleMap;

  private directionsService: google.maps.DirectionsService;
  private directionsRenderer: google.maps.DirectionsRenderer;
  private needsDirectionsCalculation = false;

  ngAfterViewInit() {
    this.initializeDirections();
    this.setupMarkerListeners();

    this.mapMarkers.changes.subscribe(() => {
      this.setupMarkerListeners();
    });

    // Set the map for directions renderer after view init
    if (this.map && this.directionsRenderer) {
      this.directionsRenderer.setMap(this.map.googleMap);

      // Calculate directions if we have origin/destination and map is ready
      if (this.needsDirectionsCalculation) {
        this.calculateDirections();
      }
    }
  }

  private initializeDirections() {
    this.directionsService = new google.maps.DirectionsService();
    this.directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: true, // Don't show default markers since we have custom ones
      polylineOptions: {
        strokeColor: '#4285F4',
        strokeWeight: 5
      }
    });
  }

  ngOnChanges(changes: any) {
    if (changes.markers && this.markers) {
      this.updateMarkersWithIcons();
    }
    if (this.mapMarkers && this.mapMarkers.length > 0) {
      this.setupMarkerListeners();
    }

    // Store that we need to calculate directions, but wait for map initialization
    if ((changes.origin && this.origin) || (changes.destination && this.destination)) {
      this.needsDirectionsCalculation = !!(this.origin && this.destination);
      if (this.map && this.needsDirectionsCalculation) {
        this.calculateDirections();
      }
    }
  }

  private setupMarkerListeners() {
    this.mapMarkers.forEach((mapMarker, index) => {
      const marker = mapMarker.marker;
      if (marker && !marker.get('dragListenerAdded')) {
        marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
          this.markerDragEnd.emit({ event, marker: this.markers[index] });
        });
        marker.set('dragListenerAdded', true);
      }
    });
  }

  private updateMarkersWithIcons() {
    this.markers.forEach(marker => {
      if (!marker.options) {
        marker.options = {};
      }

      // Set default options
      marker.options.draggable = marker.options.draggable ?? false;

      // Only apply default icons if no custom icon is provided
      if (!marker.options.icon) {
        // Apply type-specific styling
        switch (marker.type) {
          case 'start':
            marker.options.icon = {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="18" fill="#4285F4" stroke="#FFFFFF" stroke-width="3"/>
                  <text x="20" y="26" text-anchor="middle" fill="#FFFFFF" font-size="16" font-weight="bold" font-family="Arial">A</text>
                </svg>
              `),
              scaledSize: new google.maps.Size(40, 40),
              anchor: new google.maps.Point(20, 40)
            };
            break;

          case 'end':
            marker.options.icon = {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="18" fill="#EA4335" stroke="#FFFFFF" stroke-width="3"/>
                  <text x="20" y="26" text-anchor="middle" fill="#FFFFFF" font-size="16" font-weight="bold" font-family="Arial">B</text>
                </svg>
              `),
              scaledSize: new google.maps.Size(40, 40),
              anchor: new google.maps.Point(20, 40)
            };
            break;

          default:
            // Default marker styling
            marker.options.icon = marker.options.icon || undefined;
            break;
        }
      }
    });
  }  private calculateDirections() {
    if (!this.origin || !this.destination || !this.directionsService || !this.directionsRenderer) {
      return;
    }

    // Ensure directions renderer is attached to the map
    if (this.map && this.map.googleMap) {
      this.directionsRenderer.setMap(this.map.googleMap);
    } else {
      return;
    }

    const request: google.maps.DirectionsRequest = {
      origin: this.origin,
      destination: this.destination,
      travelMode: google.maps.TravelMode.DRIVING,
      unitSystem: google.maps.UnitSystem.METRIC
    };

    this.directionsService.route(request, (result, status) => {
      if (status === google.maps.DirectionsStatus.OK && result) {
        this.directionsRenderer.setDirections(result);
        this.directionsCalculated.emit({
          directions: result,
          distance: this.getTotalDistance(result),
          duration: this.getTotalDuration(result)
        });
      } else {
        console.error('Directions request failed:', status);
      }
    });
  }

  private getTotalDistance(result: google.maps.DirectionsResult): string {
    let totalDistance = 0;
    result.routes[0].legs.forEach(leg => {
      totalDistance += leg.distance?.value || 0;
    });
    return (totalDistance / 1000).toFixed(1) + ' km';
  }

  private getTotalDuration(result: google.maps.DirectionsResult): string {
    let totalDuration = 0;
    result.routes[0].legs.forEach(leg => {
      totalDuration += leg.duration?.value || 0;
    });
    return Math.round(totalDuration / 60) + ' min';
  }

  getDefaultInfoWindowContent(marker: MapMarker): string {
    const label = marker.options?.label || 'Location';
    return `<strong>${label}</strong><br>Lat: ${marker.position.lat}<br>Lng: ${marker.position.lng}`;
  }
}